import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Alert, Button, Card, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faFileInvoiceDollar, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { downloadPoSalesCsv } from '../utils/poSalesCsv';
import { API_URL, END_POINT_PO_SALES } from '../const';
import { hasUserPermission } from '../utils/userPermissions';
import CreateRecordModal from '../components/CreateRecordModal';
import PoSaleDetailSidebar from '../components/PoSaleDetailSidebar';
import PoSaleEditForm from './EditPoSale';
import PoSalesFilters from '../components/PoSalesFilters';
import { filterPoSales } from '../utils/poSalesListing';

const readToken = () => sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
const formatDate = value => value ? new Date(value).toLocaleDateString() : '-';
const formatAmount = value => '\u20b9 ' + Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

// Column key -> how to read the value and how to compare it.
const columns = [
  { label: 'Status', key: 'status', value: r => r.status },
  { label: 'PO Number', key: 'po_number', value: r => r.po_number },
  { label: 'Quote No.', key: 'quote_no', value: r => r.offer_no || r.system_quote_no },
  { label: 'Customer', key: 'customer', value: r => r.quote?.customer?.name },
  { label: 'Company', key: 'company', value: r => r.quote?.company?.name },
  { label: 'PO Release Date', key: 'po_release_date', value: r => r.po_release_date, type: 'date' },
  { label: 'PO Amount', key: 'po_amount', value: r => r.po_amount, type: 'number' },
  { label: 'Payment Term', key: 'payment_term', value: r => r.payment_term },
];

const compareValues = (a, b, type) => {
  if (type === 'number') return Number(a) - Number(b);
  if (type === 'date') return new Date(a) - new Date(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

function PoSalesListing({ token }) {
  const [poSales, setPoSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: null, direction: null });
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const deleting = useRef(false);
  const mounted = useRef(true);
  const location = useLocation();
  const openedFor = useRef(null);
  const openPoSaleId = location.state?.openPoSaleId;
  const canView = hasUserPermission('po_sales', 'view');
  const canEdit = hasUserPermission('po_sales', 'edit');
  const canDelete = hasUserPermission('po_sales', 'delete');

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrent = () => mounted.current && readToken() === token;

  useEffect(() => {
    if (!canView) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    axios.get(API_URL + END_POINT_PO_SALES, {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: controller.signal,
    }).then(response => {
      if (!active || readToken() !== token) return;
      if (!response.data.status || !Array.isArray(response.data.data)) {
        throw new Error(response.data.message || 'Unable to load PO Sales records.');
      }
      setPoSales(response.data.data);
    }).catch(requestError => {
      if (active && readToken() === token) {
        setError(requestError.response?.data?.message || requestError.message || 'Unable to load PO Sales records.');
      }
    }).finally(() => { if (active && readToken() === token) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [refreshKey, token, canView]);

  // Arriving from a Quote Details "PO Sales No." link: open that record once the list has loaded.
  useEffect(() => {
    if (!openPoSaleId || loading || openedFor.current === location.key) return;
    const match = poSales.find(record => String(record.id) === String(openPoSaleId));
    if (match) {
      openedFor.current = location.key; // open once per navigation, not again after later edits
      setSelectedId(match.id);
    }
  }, [openPoSaleId, location.key, loading, poSales]);

  const closeEditor = () => { if (!isUpdating) setEditingId(null); };
  const handleUpdated = result => {
    if (!isCurrent()) return;
    setEditingId(null);
    setMessage(result.message || 'PO Sales updated successfully.');
    if (result.data?.id) {
      setPoSales(current => current.map(record => record.id === result.data.id ? { ...record, ...result.data } : record));
    } else setRefreshKey(value => value + 1);
  };
  const handleDelete = async record => {
    if (!hasUserPermission('po_sales', 'delete') || deleting.current || !isCurrent()) return;
    deleting.current = true;
    setDeletingId(record.id);
    setActionError('');
    setMessage('');
    try {
      const confirmation = await Swal.fire({
        title: 'Delete PO Sales?', text: 'This will delete the PO Sales record for ' + (record.offer_no || record.system_quote_no || record.id) + '.',
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
      });
      if (!confirmation.isConfirmed || !isCurrent() || !hasUserPermission('po_sales', 'delete')) return;
      const response = await axios.delete(API_URL + END_POINT_PO_SALES + '/' + record.id, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      });
      if (!isCurrent()) return;
      if (!response.data.status) throw new Error(response.data.message || 'Unable to delete PO Sales.');
      setPoSales(current => current.filter(item => item.id !== record.id));
      setSelectedId(current => current === record.id ? null : current);
      setMessage(response.data.message || 'PO Sales deleted successfully.');
    } catch (requestError) {
      if (isCurrent()) setActionError(requestError.response?.data?.message || requestError.message || 'Unable to delete PO Sales.');
    } finally {
      deleting.current = false;
      if (isCurrent()) setDeletingId(null);
    }
  };

  const sortedPoSales = useMemo(() => {
    const filtered = filterPoSales(poSales, filters);
    const column = columns.find(item => item.key === sort.key);
    if (!column || !sort.direction) return filtered;
    const factor = sort.direction === 'asc' ? 1 : -1;
    const isEmpty = value => value === null || value === undefined || value === '';
    return [...filtered].sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      if (isEmpty(left) || isEmpty(right)) return isEmpty(left) === isEmpty(right) ? 0 : (isEmpty(left) ? 1 : -1); // blanks last either way
      return compareValues(left, right, column.type) * factor;
    });
  }, [poSales, sort, filters]);
  // Same cycle as the Quote Listing: ascending, descending, then back to the default order.
  const sortBy = key => {
    setSelectedId(null);
    setSort(previous => {
      const direction = previous.key === key ? (previous.direction === 'asc' ? 'desc' : null) : 'asc';
      return { key: direction ? key : null, direction };
    });
  };
  const sortArrow = key => sort.key === key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ' ↕';

  if (!canView) return <Alert variant="warning">You do not have permission to view PO Sales.</Alert>;

  const selectedIndex = sortedPoSales.findIndex(record => record.id === selectedId);
  const selected = selectedIndex >= 0 ? sortedPoSales[selectedIndex] : null;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-3">
        <h2 className="page-breadcrumb mb-0">PO Sales</h2>
        <Button variant="outline-primary" size="sm" disabled={loading || Boolean(error) || sortedPoSales.length === 0}
          onClick={() => downloadPoSalesCsv(sortedPoSales)}>
          <FontAwesomeIcon icon={faFileCsv} className="me-2" />CSV Export
        </Button>
      </div>
      {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>}
      <Card border="light" className="shadow-sm">
        <Card.Body>
          <PoSalesFilters records={poSales} filters={filters} onChange={next => { setFilters(next); setSelectedId(null); }} />
          {loading && <div className="text-center py-4" role="status">Loading PO Sales...</div>}
          {!loading && error && <Alert variant="danger">{error} <Button size="sm" variant="outline-danger" onClick={() => setRefreshKey(value => value + 1)}>Retry</Button></Alert>}
          {!loading && !error && sortedPoSales.length === 0 && <div className="text-center py-4">{poSales.length ? 'No PO Sales match the selected filters.' : 'No PO Sales records found.'}</div>}
          {!loading && !error && sortedPoSales.length > 0 && (
            <Table hover responsive className="align-items-center table-flush">
              <thead className="thead-light"><tr>
                {columns.map(({ label, key }) => <th key={key} onClick={() => sortBy(key)} style={{ cursor: 'pointer' }}
                  aria-sort={sort.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className="btn btn-link p-0 text-reset text-nowrap fw-bold" aria-label={'Sort by ' + label}
                    onClick={event => { event.stopPropagation(); sortBy(key); }}>{label}{sortArrow(key)}</button>
                </th>)}
                {(canEdit || canDelete) && <th className="text-end">Actions</th>}
              </tr></thead>
              <tbody>{sortedPoSales.map(record => (
                <tr key={record.id} onClick={() => setSelectedId(record.id)}
                  style={{ cursor: 'pointer' }}
                  className={selectedId === record.id ? 'table-active' : ''}>
                  <td>{record.status || '-'}</td>
                  <td>{record.po_number || '-'}</td>
                  <td>{record.offer_no || record.system_quote_no || '-'}</td>
                  <td>{record.quote?.customer?.name || '-'}</td>
                  <td>{record.quote?.company?.name || '-'}</td>
                  <td>{formatDate(record.po_release_date)}</td>
                  <td>{formatAmount(record.po_amount)}</td><td>{record.payment_term || '-'}</td>
                  {(canEdit || canDelete) && <td onClick={event => event.stopPropagation()}>
                    <div className="d-flex justify-content-end align-items-center gap-3 flex-nowrap">
                      {canEdit && <Button variant="link" className="p-0 text-success"
                        disabled={deletingId !== null} title="Edit PO Sales" aria-label={'Edit PO Sales ' + record.id}
                        onClick={() => { if (hasUserPermission('po_sales', 'edit')) { setMessage(''); setActionError(''); setEditingId(record.id); } }}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>}
                      {canDelete && <Button variant="link" className="p-0 text-danger"
                        disabled={deletingId !== null} title="Delete PO Sales" aria-label={'Delete PO Sales ' + record.id}
                        onClick={() => handleDelete(record)}><FontAwesomeIcon icon={faTrash} /></Button>}
                    </div>
                  </td>}
                </tr>
              ))}</tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <CreateRecordModal show={editingId !== null && canEdit} onHide={closeEditor}
        isSubmitting={isUpdating} titleId="edit-po-sales-title" title="Edit PO Sales"
        description="Update purchase order details, payment terms, and status." icon={faFileInvoiceDollar} size="lg" dialogClassName="po-sales-modal-wide">
        {editingId !== null && canEdit && <PoSaleEditForm key={editingId} poSaleId={editingId}
          onSuccess={handleUpdated} onCancel={closeEditor} onSubmittingChange={setIsUpdating} />}
      </CreateRecordModal>
      <PoSaleDetailSidebar
        record={selected}
        show={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        onEdit={id => { setMessage(''); setActionError(''); setEditingId(id); }}
        onDelete={handleDelete}
        canEdit={canEdit}
        canDelete={canDelete}
        deleting={deletingId !== null}
        onPrevious={() => selectedIndex > 0 && setSelectedId(sortedPoSales[selectedIndex - 1].id)}
        onNext={() => selectedIndex >= 0 && selectedIndex < sortedPoSales.length - 1 && setSelectedId(sortedPoSales[selectedIndex + 1].id)}
        hasPrevious={selectedIndex > 0}
        hasNext={selectedIndex >= 0 && selectedIndex < sortedPoSales.length - 1}
      />
    </>
  );
}

export default function PoSales() {
  const token = readToken();
  return <PoSalesListing key={token || 'signed-out'} token={token} />;
}
